import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../services/AppSettingsContext';

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto' },
  backLink: { color: '#0078d4', cursor: 'pointer', fontSize: 14, marginBottom: 16, display: 'inline-block', background: 'none', border: 'none', padding: 0 },
  title: { fontSize: 28, fontWeight: 700, color: '#1a365d', margin: '0 0 8px' },
  subtitle: { color: '#666', fontSize: 14, margin: '0 0 32px' },
  toc: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '20px 24px', marginBottom: 32 },
  tocTitle: { fontSize: 14, fontWeight: 700, color: '#1a365d', margin: '0 0 12px' },
  tocList: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' },
  tocLink: { color: '#0078d4', fontSize: 13, cursor: 'pointer', textDecoration: 'none', padding: '2px 0', display: 'block' },
  section: { marginBottom: 36 },
  sectionTitle: { fontSize: 20, fontWeight: 700, color: '#1a365d', margin: '0 0 12px', paddingBottom: 8, borderBottom: '2px solid #e2e8f0' },
  subsection: { fontSize: 16, fontWeight: 600, color: '#2d3748', margin: '20px 0 8px' },
  p: { fontSize: 14, lineHeight: 1.7, color: '#4a5568', margin: '0 0 12px' },
  ul: { paddingLeft: 20, margin: '0 0 12px' },
  li: { fontSize: 14, lineHeight: 1.7, color: '#4a5568', marginBottom: 4 },
  tip: { background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#2a4365' },
  warning: { background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#92400e' },
  kbd: { background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: 3, padding: '1px 6px', fontSize: 12, fontFamily: 'monospace' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13, marginBottom: 16 },
  th: { textAlign: 'left' as const, padding: '10px 12px', background: '#f7fafc', borderBottom: '2px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#666' },
  td: { padding: '10px 12px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top' as const },
  searchBox: { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e0', borderRadius: 8, fontSize: 14, marginBottom: 24, boxSizing: 'border-box' as const },
};

const SECTIONS = [
  { id: 'getting-started', title: 'Getting Started' },
  { id: 'dashboard', title: 'Dashboard' },
  { id: 'creating-reports', title: 'Creating Expense Reports' },
  { id: 'receipts', title: 'Managing Receipts' },
  { id: 'line-items', title: 'Line Items & Categories' },
  { id: 'mileage', title: 'Mileage Tracking' },
  { id: 'report-history', title: 'Report History' },
  { id: 'tips', title: 'Tips & Best Practices' },
  { id: 'faq', title: 'FAQ' },
  { id: 'shortcuts', title: 'Keyboard Shortcuts' },
];

export default function HelpPage() {
  const { mileageRate } = useAppSettings();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const matchesSearch = (text: string) => {
    if (!search) return true;
    return text.toLowerCase().includes(search.toLowerCase());
  };

  return (
    <div style={styles.container}>
      <button style={styles.backLink} onClick={() => navigate(-1)}>
        &larr; Back
      </button>

      <h1 style={styles.title}>Documentation</h1>
      <p style={styles.subtitle}>Everything you need to know about the CGS Expense Reports app &mdash; without the boring parts.</p>

      <input
        style={styles.searchBox}
        placeholder="Search documentation..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {!search && (
        <div style={styles.toc}>
          <h2 style={styles.tocTitle}>Table of Contents</h2>
          <ul style={styles.tocList}>
            {SECTIONS.map(s => (
              <li key={s.id}>
                <a style={styles.tocLink} onClick={() => scrollTo(s.id)}>{s.title}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Getting Started */}
      {matchesSearch('getting started login sign in authentication overview') && (
        <div style={styles.section} id="getting-started">
          <h2 style={styles.sectionTitle}>Getting Started</h2>
          <p style={styles.p}>
            CGS Expense Reports takes the pain out of creating GUSTO reimbursement reports. Upload your receipts, let the app do the formatting, and walk away with a clean PDF and Excel file. That's it. That's the app.
          </p>
          <h3 style={styles.subsection}>Signing In</h3>
          <p style={styles.p}>
            Sign in with your Cultivate Geospatial Microsoft account &mdash; the same one you use for email and everything else. SSO handles the rest. If you can get into Outlook, you can get in here.
          </p>
          <h3 style={styles.subsection}>First Time Here?</h3>
          <p style={styles.p}>
            The first time you sign in, you'll get a quick guided tour of the main areas. It takes about 30 seconds and saves you from the "where do I even start?" feeling. You can replay it anytime from the ? icon in the top-right corner.
          </p>
          <h3 style={styles.subsection}>The Big Picture</h3>
          <p style={styles.p}>There are three main areas you'll use:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Dashboard</strong> &mdash; Your home page with a gallery of all your uploaded receipts</li>
            <li style={styles.li}><strong>New Report</strong> &mdash; The step-by-step wizard for building an expense report (and where you upload receipt files)</li>
            <li style={styles.li}><strong>History</strong> &mdash; Every report you've ever generated, ready to download or edit</li>
          </ul>
        </div>
      )}

      {/* Dashboard */}
      {matchesSearch('dashboard receipts gallery overview home') && (
        <div style={styles.section} id="dashboard">
          <h2 style={styles.sectionTitle}>Dashboard</h2>
          <p style={styles.p}>
            The Dashboard is your landing page. Think of it as your "at a glance" view &mdash; it shows all the receipts you've uploaded, organized in a nice gallery with preview thumbnails. You can quickly check what's there before you start building a report.
          </p>
          <h3 style={styles.subsection}>Receipt Gallery</h3>
          <p style={styles.p}>
            Each receipt card shows the vendor name, date, amount, and a color-coded category. Click any receipt to preview it full-size. If the AI got the amount wrong (it happens), you can fix it right from the preview.
          </p>
          <div style={styles.tip}>
            <strong>Pro tip:</strong> If a receipt looks like it's in the wrong category, don't worry about it here. You'll get a chance to fix categories when you're building the actual report in the line items step.
          </div>
        </div>
      )}

      {/* Creating Reports */}
      {matchesSearch('creating reports new report expense report wizard steps generate pdf excel upload') && (
        <div style={styles.section} id="creating-reports">
          <h2 style={styles.sectionTitle}>Creating Expense Reports</h2>
          <p style={styles.p}>
            This is where the real work happens &mdash; except the app does most of it for you. Creating a report is a 4-step wizard. Now, sit back and relax as I walk you through the whole process.
          </p>

          <h3 style={styles.subsection}>Step 1: Date Range & Details</h3>
          <p style={styles.p}>Set up the basics:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Employee Name</strong> &mdash; Pre-filled from your account. Change it if you're submitting for someone else.</li>
            <li style={styles.li}><strong>Report Title</strong> &mdash; Something descriptive like "Esri Dev Summit March 2026." Future-you will thank present-you.</li>
            <li style={styles.li}><strong>Date Range</strong> &mdash; The start and end dates of your expense period. This determines which receipts show up in the next step.</li>
            <li style={styles.li}><strong>Reason</strong> &mdash; Pick from: Conference Attendee, Conference Exhibitor, Client Onsite, or N/A.</li>
          </ul>
          <p style={styles.p}>
            This is also where you <strong>upload your receipt files</strong> &mdash; drag and drop them in or click to browse. The app uses AI to read each receipt and pull out the vendor, date, and amount automatically. Supported formats: JPEG, PNG, and PDF.
          </p>

          <h3 style={styles.subsection}>Step 2: Select Receipts</h3>
          <p style={styles.p}>
            All your uploaded receipts within the date range show up here. Click each one to select or deselect it &mdash; selected receipts get a blue border so you can see what's in and what's out. Easy.
          </p>

          <h3 style={styles.subsection}>Step 3: Review Line Items</h3>
          <p style={styles.p}>
            This is the detail screen. Each selected receipt becomes a line item with the vendor, date, category, and amount pre-filled. You can edit anything, add new items, remove items you don't want, and add mileage from saved routes. The app also warns you about common issues &mdash; missing receipts, missing meal days, that kind of thing.
          </p>

          <h3 style={styles.subsection}>Step 4: Generate Report</h3>
          <p style={styles.p}>
            Review your totals, take a deep breath, and click Generate. The system creates a GUSTO-formatted Excel spreadsheet and a combined PDF with the expense sheet plus all your receipt images. Both files are ready for immediate download. You just saved yourself an hour of manual formatting. You're welcome.
          </p>
        </div>
      )}

      {/* Receipts */}
      {matchesSearch('receipts upload manage preview ocr extract vendor amount date') && (
        <div style={styles.section} id="receipts">
          <h2 style={styles.sectionTitle}>Managing Receipts</h2>
          <p style={styles.p}>
            Receipts are the foundation of everything here. Get them into the system, and the app handles the rest.
          </p>

          <h3 style={styles.subsection}>Uploading</h3>
          <p style={styles.p}>
            Upload receipts during Step 1 of the New Report wizard. Drag and drop files into the upload area, or click to browse your computer. Supported formats: JPEG, PNG, and PDF, up to 10MB each.
          </p>

          <h3 style={styles.subsection}>AI-Powered OCR</h3>
          <p style={styles.p}>
            When you upload a receipt, AI reads the image and pulls out the vendor name, date, and total amount. It also figures out if it's a directions screenshot from Google Maps (for mileage tracking). Is it perfect every time? Nope. But it's right often enough to save you a lot of typing, and you can always fix anything it gets wrong.
          </p>
          <div style={styles.tip}>
            <strong>Pro tip:</strong> Clear photos with good lighting get the best OCR results. If you're photographing a receipt on your phone, try to avoid shadows and weird angles. The AI is smart, but it's not magic. (Close, though.)
          </div>

          <h3 style={styles.subsection}>Receipt Preview</h3>
          <p style={styles.p}>
            Click any receipt &mdash; on the Dashboard or in the report wizard &mdash; to open a full-size preview. From there you can edit the amount (just click the dollar value) or open it in a new tab for printing.
          </p>

          <h3 style={styles.subsection}>Auto-Categorization</h3>
          <p style={styles.p}>
            The app auto-categorizes receipts based on the vendor name. Airline name? Airfare. Hotel chain? Lodging. Uber or Lyft? Transportation. Everything else defaults to Meals. You can always change the category in the line items step.
          </p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>How It's Detected</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={styles.td}><strong>Meals</strong></td><td style={styles.td}>Default for recognized vendors</td></tr>
              <tr><td style={styles.td}><strong>Airfare</strong></td><td style={styles.td}>Delta, United, American, Southwest, JetBlue, etc.</td></tr>
              <tr><td style={styles.td}><strong>Lodging</strong></td><td style={styles.td}>Hotel, Marriott, Hilton, Hyatt, Hampton, etc.</td></tr>
              <tr><td style={styles.td}><strong>Transportation</strong></td><td style={styles.td}>Lyft, Uber, Taxi, Toll, Parking</td></tr>
              <tr><td style={styles.td}><strong>Rental Car</strong></td><td style={styles.td}>Hertz, Avis, Enterprise, Budget, National</td></tr>
              <tr><td style={styles.td}><strong>Mileage</strong></td><td style={styles.td}>Google Maps direction screenshots</td></tr>
              <tr><td style={styles.td}><strong>Other</strong></td><td style={styles.td}>Conference Reg, Office Supplies, etc.</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Line Items */}
      {matchesSearch('line items categories edit vendor amount expense gusto') && (
        <div style={styles.section} id="line-items">
          <h2 style={styles.sectionTitle}>Line Items & Categories</h2>
          <p style={styles.p}>
            Each line item is one row in your GUSTO expense report. Here's what you're working with:
          </p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Field</th>
                <th style={styles.th}>What It Does</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={styles.td}><strong>Receipt</strong></td><td style={styles.td}>Category icon + "View" link to preview the attached receipt</td></tr>
              <tr><td style={styles.td}><strong>Date</strong></td><td style={styles.td}>When you spent the money</td></tr>
              <tr><td style={styles.td}><strong>Vendor</strong></td><td style={styles.td}>Who you gave it to</td></tr>
              <tr><td style={styles.td}><strong>Category</strong></td><td style={styles.td}>The GUSTO category (Meals, Airfare, Lodging, etc.)</td></tr>
              <tr><td style={styles.td}><strong>Mileage</strong></td><td style={styles.td}>Miles driven &mdash; only for Mileage category items</td></tr>
              <tr><td style={styles.td}><strong>Round Trip</strong></td><td style={styles.td}>Check this to double the mileage automatically</td></tr>
              <tr><td style={styles.td}><strong>Amount</strong></td><td style={styles.td}>Dollar amount (auto-calculated for mileage items)</td></tr>
            </tbody>
          </table>

          <h3 style={styles.subsection}>The Full Category List</h3>
          <p style={styles.p}>These match the GUSTO dropdown exactly:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Meals, Airfare, Lodging, Transportation, Mileage, Rental Car</li>
            <li style={styles.li}>Computer + Office Supplies, Learning + Development, Conference Registration</li>
            <li style={styles.li}>Memberships + Subscriptions, Service Fees, Alcohol, Other</li>
          </ul>
          <div style={styles.warning}>
            <strong>Heads up:</strong> Alcohol is only reimbursable for client entertainment by owners and sales staff. And remember &mdash; submit within 30 days of travel/purchase or it's on you.
          </div>
        </div>
      )}

      {/* Mileage */}
      {matchesSearch('mileage route driving round trip rate miles saved routes directions') && (
        <div style={styles.section} id="mileage">
          <h2 style={styles.sectionTitle}>Mileage Tracking</h2>
          <p style={styles.p}>
            Mileage tracking used to be the worst part of expense reports. We fixed that. (Ask me how long it took to get right. On second thought, don't.)
          </p>

          <h3 style={styles.subsection}>The Rate</h3>
          <p style={styles.p}>
            Current reimbursement rate: <strong>${mileageRate.toFixed(3)} per mile</strong>. The app calculates the dollar amount for you automatically &mdash; just enter the miles and it does the math. The amount field is locked for mileage items so you can't accidentally override it.
          </p>

          <h3 style={styles.subsection}>Adding Mileage</h3>
          <p style={styles.p}>Three ways to do it:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Change any line item's category to "Mileage" and type in the miles</li>
            <li style={styles.li}>Click a saved route button to add a pre-configured mileage entry in one click</li>
            <li style={styles.li}>Check "Round Trip" to automatically double the distance &mdash; because nobody wants to add two separate line items for the same drive</li>
          </ul>

          <h3 style={styles.subsection}>Saved Routes</h3>
          <p style={styles.p}>
            If you drive the same routes regularly (home to airport, office to client site), save them. Each saved route stores the origin, destination, default mileage, round-trip setting, and even an optional directions receipt. Next time you need that route, it's one click.
          </p>
          <div style={styles.tip}>
            <strong>Pro tip:</strong> Attach a Google Maps directions PDF to your saved routes. The receipt requirement for mileage is satisfied automatically every time you use that route. Set it once, forget it forever.
          </div>

          <h3 style={styles.subsection}>How the Math Works</h3>
          <p style={{ ...styles.p, fontFamily: 'monospace', background: '#f7fafc', padding: '8px 12px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
            Amount = Miles x (Round Trip ? 2 : 1) x ${mileageRate.toFixed(3)}
          </p>
          <p style={styles.p}>
            45 miles one-way = $31.50. 45 miles round-trip = $63.00. That's it. That's the math.
          </p>
        </div>
      )}

      {/* Report History */}
      {matchesSearch('history reports view edit download regenerate pdf excel') && (
        <div style={styles.section} id="report-history">
          <h2 style={styles.sectionTitle}>Report History</h2>
          <p style={styles.p}>
            Every report you've ever generated lives on the History page. Think of it as your filing cabinet &mdash; except it's actually organized and you can find things in it.
          </p>

          <h3 style={styles.subsection}>What You Can Do</h3>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>View</strong> &mdash; Click any report to see the full details with all receipts and line items</li>
            <li style={styles.li}><strong>Download</strong> &mdash; Grab the PDF (expense sheet + all receipts) or the Excel file (GUSTO spreadsheet only)</li>
            <li style={styles.li}><strong>Edit & Regenerate</strong> &mdash; Made a mistake? Click into the report, fix whatever needs fixing, and hit "Save & Regenerate." The app rebuilds the PDF and Excel files on the spot. No need to start over.</li>
          </ul>
        </div>
      )}

      {/* Tips */}
      {matchesSearch('tips best practices policy submit receipts deadline') && (
        <div style={styles.section} id="tips">
          <h2 style={styles.sectionTitle}>Tips & Best Practices</h2>
          <p style={styles.p}>Hard-won wisdom from people who've been through this before:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Upload receipts as you go</strong> &mdash; Don't wait until the end of a trip. Upload them while you still know what they are.</li>
            <li style={styles.li}><strong>Use descriptive report titles</strong> &mdash; "Esri DevSummit March 2026" beats "Expenses" every time. Trust us.</li>
            <li style={styles.li}><strong>Save your common mileage routes</strong> &mdash; If you drive to the airport twice a month, save that route once and never think about it again.</li>
            <li style={styles.li}><strong>Attach mileage receipts to routes</strong> &mdash; One Google Maps PDF, saved to the route, covers you every time. Forever.</li>
            <li style={styles.li}><strong>Check the warnings</strong> &mdash; The line items step flags missing receipts, missing meal days, and other common issues. Don't ignore them.</li>
            <li style={styles.li}><strong>Submit within 30 days</strong> &mdash; Company policy. The app can't enforce it, but your accountant will.</li>
            <li style={styles.li}><strong>One transaction per line item</strong> &mdash; Keep it clean. Meals from the same receipt can be combined, but otherwise &mdash; one purchase, one line.</li>
            <li style={styles.li}><strong>Max 34 line items per report</strong> &mdash; That's a GUSTO template constraint. If you somehow have more than 34 line items, create a second report for the overflow.</li>
          </ul>
        </div>
      )}

      {/* FAQ */}
      {matchesSearch('faq frequently asked questions help troubleshooting') && (
        <div style={styles.section} id="faq">
          <h2 style={styles.sectionTitle}>FAQ</h2>
          <FAQItem
            q="What file formats work for receipts?"
            a="JPEG, PNG, and PDF &mdash; up to 10MB each. If you can photograph it or scan it, we can handle it."
          />
          <FAQItem
            q="Can I edit a report after I've generated it?"
            a="Absolutely. Go to History, click the report, fix whatever you need, and hit 'Save & Regenerate.' Fresh PDF and Excel files, just like that."
          />
          <FAQItem
            q="What's the mileage rate?"
            a={`$${mileageRate.toFixed(3)} per mile. Round-trip entries automatically double the distance before calculating.`}
          />
          <FAQItem
            q="Why can I only have 34 line items?"
            a="That's how many rows the GUSTO template has. If you need more (impressive, honestly), create a second report."
          />
          <FAQItem
            q="What happens to receipts when I delete a report?"
            a="Receipts are safe &mdash; they stay in your gallery. Only the report itself and its generated files are removed."
          />
          <FAQItem
            q="How does the AI categorize receipts?"
            a="It reads the receipt image, identifies the vendor name, and matches it to a category based on keywords. Airline? Airfare. Hotel? Lodging. It's not rocket science, but it gets it right most of the time. You can always fix it."
          />
          <FAQItem
            q="Does it work on mobile?"
            a="Yep. The app is responsive, so you can photograph receipts on your phone and upload them straight from the browser. It's not the prettiest experience on a small screen, but it gets the job done."
          />
          <FAQItem
            q="Who can see my reports?"
            a="Only you &mdash; unless you have an admin. Admin users with Accounting access can see all reports across the organization for review purposes."
          />
          <FAQItem
            q="How do I get admin access?"
            a="Ask someone who already has it. They can add your @cultivategeospatial.com email from the Accounting > Admins tab."
          />
        </div>
      )}

      {/* Shortcuts */}
      {matchesSearch('keyboard shortcuts hotkeys navigation keys') && (
        <div style={styles.section} id="shortcuts">
          <h2 style={styles.sectionTitle}>Keyboard Shortcuts</h2>
          <p style={styles.p}>For the keyboard warriors among us:</p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Shortcut</th>
                <th style={styles.th}>What It Does</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={styles.td}><span style={styles.kbd}>Esc</span></td><td style={styles.td}>Close any modal, dropdown, or dialog</td></tr>
              <tr><td style={styles.td}><span style={styles.kbd}>Enter</span></td><td style={styles.td}>Confirm / submit / next step in tour</td></tr>
              <tr><td style={styles.td}><span style={styles.kbd}>&rarr;</span></td><td style={styles.td}>Next step in guided tour</td></tr>
              <tr><td style={styles.td}><span style={styles.kbd}>&larr;</span></td><td style={styles.td}>Previous step in guided tour</td></tr>
              <tr><td style={styles.td}><span style={styles.kbd}>Ctrl</span> + Click</td><td style={styles.td}>Multi-select chart segments in Accounting</td></tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{ padding: '24px 0 48px', borderTop: '1px solid #e2e8f0', marginTop: 32, textAlign: 'center', color: '#999', fontSize: 12 }}>
        CGS Expense Reports &middot; Cultivate Geospatial Solutions
      </div>
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: 0 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', textAlign: 'left', padding: '14px 0', background: 'none',
          border: 'none', fontSize: 14, fontWeight: 600, color: '#2d3748', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        {q}
        <span style={{ color: '#999', fontSize: 18, transform: open ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }}>+</span>
      </button>
      {open && (
        <p style={{ fontSize: 14, lineHeight: 1.7, color: '#4a5568', margin: '0 0 14px', paddingBottom: 0 }}>{a}</p>
      )}
    </div>
  );
}
